/**
 * /ja/decision-log — 意思決定ログ concept page (JA, single-locale).
 *
 * The Draftlize wedge concept. Mirrors the EN /decision-log angle ("the living
 * kind that doesn't rot"). Links to the /ja/meeting-minutes-ai bridge.
 */
import { MarketingShell } from './MarketingShell';

export function JaDecisionLog() {
  return (
    <MarketingShell
      currentLocale="ja"
      hreflangPath="/decision-log"
      hreflangLocales={['ja']}
      documentTitle="意思決定ログ（Decision Log）とは｜腐らない運用の作り方 · Draftlize"
      documentDescription="「なぜそう決めたか」を残す意思決定ログ。多くは書いて終わりで腐ります。決定をカード化し、変われば依存先が自動で stale になる「生きた」意思決定ログの作り方。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">ガイド · 意思決定ログ</span>
          <h1 className="serif" style={{ fontSize: 'clamp(32px, 4.4vw, 54px)', lineHeight: 1.08, margin: '12px 0 24px' }}>
            意思決定ログ——<br />
            <em>「なぜこうしたんだっけ？」に答える。</em>
          </h1>
          <p className="lede">
            意思決定ログ（ディシジョンログ）は、プロダクトの一つひとつの決定について「<em>なぜ</em>そう決めたか」を残す台帳です。数か月後、誰かが同じ議論を蒸し返したとき、当時の文脈・選択肢・トレードオフがそこにあれば一瞬で決着します。問題は、多くの意思決定ログが<em>腐る</em>こと——一度書かれ、二度と読まれず、静かに古くなる。ここでは書き方と、腐らせないための「生きた」運用を解説します。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">意思決定ログに何を記録するか。</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>決定</strong>——何を決めたか、一文で。</li>
            <li><strong>文脈</strong>——なぜ今この決定が必要だったか。</li>
            <li><strong>選択肢とトレードオフ</strong>——何を検討し、なぜ他を捨てたか。<em>ここが後で一番効く</em>。</li>
            <li><strong>担当者と日付</strong>——誰が、いつ。</li>
            <li><strong>影響範囲</strong>——どの仕様・決定に関わるか。</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            この形式は ADR（Architecture Decision Record）とも重なります。多くの決定は会議で生まれます——まず <a href="/ja/meeting-minutes-ai" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>議事録</a> から決定を取り出すのが出発点です。
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">なぜ意思決定ログは腐るのか。<em>そして、腐らせない方法。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            フラットな文書の意思決定ログは、書いた瞬間から現実と乖離し始めます。後の決定が前の決定を覆しても、文書は黙って古いまま。Draftlize では決定が"生きたカード"になります。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">決定は参照できるカード</h3>
            <p>一つの決定＝一枚のカード。文脈・選択肢・理由・担当者を持ち、ID で参照できます。文書のどこかに埋もれる代わりに、いつでも指し示せます。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">覆された決定は自動で stale</h3>
            <p>新しい決定が古い決定を覆すと、リンクされた古い決定と、その上に立つ仕様が自動で stale 表示に。「もう決めたこと」を蒸し返しません。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI エージェントが読み書きする</h3>
            <p>意思決定ログは Claude Code や Cursor が MCP 経由で読むコンテキストに。エージェントは「なぜそう決めたか」を踏まえて次を書けます。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          ほとんどの意思決定ログは、書いた次の日から誰も読まない。だから半年後、同じ議論をゼロからやり直す。
        </blockquote>
        <cite>決定を文脈ごと残し、変わったら影響範囲を自動で知らせる。それが Draftlize。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">よくある質問。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '意思決定ログと議事録の違いは？', a: '議事録は「会議の記録」、意思決定ログは「決定の台帳」です。議事録から決定だけを抜き出して蓄積するのが意思決定ログで、後から本当に役立つのはこちらです。' },
            { q: '誰が書くべき？', a: 'その決定に責任を持つ人（PM やリード）が担当するのが一般的です。大事なのは、決定の「なぜ」が後から辿れる状態を保つことです。' },
            { q: 'どのくらい詳しく書く？', a: '決定・文脈・トレードオフが分かれば十分です。長さより、覆されたときに気づける仕組みのほうが重要です。' },
            { q: 'ADR とは違う？', a: 'ADR は技術的な意思決定ログの一種です。Draftlize は ADR もプロダクトの決定も同じ「生きたカード」として扱います。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.9, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">腐らない意思決定ログを<em>——$5 無料で。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新規登録で $5 付与</span>
          <span>使った分だけ支払い</span>
          <span>残高は無期限</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            決定を文脈ごとカードにして、覆されたら自動で stale。AI エージェントも読める、腐らない意思決定ログを Draftlize で。
          </p>
          <a className="btn-primary" href="/">$5 無料で始める</a>
        </div>
      </section>
    </MarketingShell>
  );
}
